import type { ConventionalUsReportRepository } from "../../repositories/operational-repositories.js";
import {
  formatUscLaudo,
  formatUscTechnique,
  jointTypesForDocument,
  normalizeConventionalUsReport,
  validateConventionalUsReportCompleteness,
  type ConventionalUsDocumentModel,
  type ConventionalUsReport
} from "../../domain/ndt/conventional-us-report-domain.js";
import { usJointLabel } from "../../domain/ndt/us-joint-types.js";
import type { NdtDocumentCommon, NdtReportCommon, NdtValidationResult } from "../../domain/ndt/ndt-report-common.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { extractCatalogCode } from "../../domain/ndt/ndt-technical-catalog.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { NdtReportServiceBase, type NdtInspectionLookup, type NdtReportSeed } from "./ndt-report-service-base.js";

export type ConventionalUsReportSeed = NdtReportSeed;

/**
 * Relatório de Ensaio por Ultrassom Convencional — domínio e documento
 * próprios (FORM-USC), independente do ME. Os quatro Tipos de Junta entram
 * no modelo documental com a geometria do momento (SVG), marcados os usados.
 */
export class ConventionalUsReportService extends NdtReportServiceBase<ConventionalUsReport, ConventionalUsDocumentModel> {
  constructor(repo: ConventionalUsReportRepository, identityRepo?: any, inspectionLookup?: NdtInspectionLookup) {
    super(repo, {
      documentType: "USC",
      numberPrefix: "USC",
      reportLabel: "Relatório US-Convencional",
      permissions: {
        view: PermissionCode.UscReportView,
        create: PermissionCode.UscReportCreate,
        edit: PermissionCode.UscReportEdit,
        validate: PermissionCode.UscReportValidate,
        approve: PermissionCode.UscReportApprove,
        sign: PermissionCode.UscReportSign,
        manageSignatories: PermissionCode.UscReportManageSignatories
      }
    }, identityRepo, inspectionLookup);
  }

  protected newReport(base: NdtReportCommon<ConventionalUsDocumentModel>, _seed: ConventionalUsReportSeed | undefined): ConventionalUsReport {
    return {
      ...base,
      surfaceCondition: "",
      technique: "",
      techniqueOther: "",
      couplantId: null,
      couplant: "",
      deviceAssetId: null,
      deviceManufacturer: "",
      deviceModel: "",
      deviceSerialNumber: "",
      deviceCalibrationCert: "",
      deviceCalibrationDate: "",
      deviceCalibrationValidity: "",
      calibrationBlockAssetId: null,
      calibrationBlock: "",
      calibrationBlockType: "",
      calibrationBlockCert: "",
      calibrationBlockCertValidity: "",
      verificationReference: "",
      calibrationNote: "",
      transducers: [],
      jointTypes: [],
      results: []
    };
  }

  protected normalize(report: ConventionalUsReport): ConventionalUsReport { return normalizeConventionalUsReport(report); }
  protected validateCompleteness(report: ConventionalUsReport): NdtValidationResult { return validateConventionalUsReportCompleteness(report); }

  protected async composeSpecific(report: ConventionalUsReport, common: NdtDocumentCommon): Promise<ConventionalUsDocumentModel> {
    return {
      ...common,
      testData: {
        procedure: report.procedureRevision,
        referenceNorm: extractCatalogCode(report.referenceNorm),
        acceptanceCriteria: report.acceptanceCriteria,
        baseMaterial: report.baseMaterial,
        surfaceCondition: report.surfaceCondition,
        testTemperature: report.testTemperature ? `${report.testTemperature} °C` : "",
        technique: formatUscTechnique(report.technique, report.techniqueOther),
        couplant: report.couplant
      },
      equipment: {
        device: [report.deviceManufacturer, report.deviceModel, report.deviceSerialNumber].filter(Boolean).join(" / "),
        deviceCertificate: [report.deviceCalibrationCert, report.deviceCalibrationValidity ? `validade ${formatCivilDateBR(report.deviceCalibrationValidity)}` : ""].filter(Boolean).join(" / "),
        calibrationBlock: [report.calibrationBlock, report.calibrationBlockType].filter(Boolean).join(" / "),
        calibrationBlockCertificate: [report.calibrationBlockCert, report.calibrationBlockCertValidity ? `validade ${formatCivilDateBR(report.calibrationBlockCertValidity)}` : ""].filter(Boolean).join(" / "),
        verificationReference: report.verificationReference,
        note: report.calibrationNote
      },
      transducers: report.transducers.map((t, i) => ({ number: String(i + 1).padStart(2, "0"), type: t.type, frequency: t.frequency, angle: t.angle, dimension: t.dimension, serialNumber: t.serialNumber })),
      jointTypes: jointTypesForDocument(report),
      results: report.results.map(r => ({
        item: r.item,
        jointRegion: r.jointRegion,
        jointType: usJointLabel(r.jointTypeId),
        inspectionPoint: r.inspectionPoint === null ? "" : String(r.inspectionPoint),
        location: r.location,
        indication: r.indication,
        sizing: r.sizing,
        laudo: r.laudo,
        laudoLabel: formatUscLaudo(r.laudo)
      }))
    };
  }
}
